<template>
  <div class="bi-emitter-summary">
    <div class="__header">
      <BaseDatePicker
        variant="full"
        @on-save-dates="updateDateRange"
        :startDate="dates[0]"
        :endDate="dates[1]"
        :datePickerValues="datePickerPeriod"
      />
    </div>
    <div class="__content">
      <div class="__highlighted">
        <BaseLoadingValue :state-loading="!emitterName">
          <template slot="content">
            <div class="__title">{{ emitterName }}</div>
          </template>
        </BaseLoadingValue>
        <BIEmitterSummarySection :data="emitterSummary" :allowEdit="true" @on-edit-clicked="onEditClicked" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { bus } from '@/main';
import BIEmitterSummarySection from './BIEmitterSummarySection.vue';
import BaseLoadingValue from '@/components/base/BaseLoadingValue.vue';
import BaseDatePicker from '@/components/base/BaseDatePicker.vue';
import { BI_DATE_PICKER_PERIOD } from '@/util/constants/date-picker-periods';

export default {
  name: 'BIEmitterSummary',
  components: {
    BaseDatePicker,
    BaseLoadingValue,
    BIEmitterSummarySection,
  },
  props: {
    dates: {
      type: Array,
      required: true,
    },
  },
  watch: {
    dates() {
      this.fetchSummary();
    },
  },
  computed: {
    ...mapGetters('biDashboard', ['emitter', 'emitterPerformanceSummary']),
    emitterId() {
      return this.$route.params.emitterId;
    },
    emitterName() {
      // the summary once loaded, else the emitter itself, else the name passed along by the list
      if (this.emitterSummary) {
        return this.emitterSummary.name;
      }
      if (this.emitter && this.emitter.id === this.emitterId) {
        return this.emitter.name;
      }
      return this.$route.query.name ? this.$route.query.name : undefined;
    },
    emitterSummary() {
      if (!this.emitterPerformanceSummary) return undefined;
      return this.emitterPerformanceSummary.emitter;
    },
    datePickerPeriod() {
      return BI_DATE_PICKER_PERIOD;
    },
  },
  methods: {
    ...mapActions('biDashboard', ['getEmitterPerformanceSummary']),
    fetchSummary() {
      this.getEmitterPerformanceSummary({
        emitterId: this.emitterId,
        startDate: this.dates[0],
        endDate: this.dates[1],
      });
    },
    updateDateRange(dates) {
      this.$emit('on-date-changed', dates);
    },
    onEditClicked() {
      this.$emit('on-edit-clicked');
    },
  },
  async created() {
    this.fetchSummary();
  },
  async mounted() {
    bus.$on('refresh-bi-dashboard', this.fetchSummary);
  },
  async beforeDestroy() {
    bus.$off('refresh-bi-dashboard', this.fetchSummary);
  },
};
</script>

<style lang="scss">
.bi-emitter-summary {
  background-color: $color-white;
  padding: 24px;
  border-radius: $border-radius-root;
  margin-bottom: 24px;

  > .__header {
    display: flex;
    justify-content: flex-end;
  }

  > .__content {
    > div {
      padding: 16px;
      display: flex;
      margin-top: 16px;

      & > :first-child {
        width: 200px;
      }

      & > :last-child {
        flex-grow: 1;
      }

      &.__highlighted {
        background-color: $color-accent-pale;
        border-radius: $border-radius-root;
        border: 1px dashed $color-accent;
      }

      .__title {
        margin-right: 16px;
        font-weight: bold;
        font-size: $font-size-root;
      }
    }
  }

  // Phones: as BICompetitorSummary's card, the name above its figures.
  @include screen-below('tablet') {
    padding: 16px;

    > .__content > div {
      flex-direction: column;
      padding: 12px;

      & > :first-child {
        width: auto;
        margin-bottom: 12px;
      }
    }
  }
}
</style>
